import React, { useEffect, useState } from 'react';
import {
  ComplementReplicateRequest,
  CustomerType,
  WorkOrder,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import {
  WorkOrderComplement,
  WorkOrderComplementTankLevel,
  WorkOrderComplementType,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { Divider, Form, Modal, ModalProps, Skeleton } from 'antd';

import { useWorkorderDetailContext } from '@/components/Movements/WorkOrders/Details/WorkorderDetailProvider';
import moment, { Moment } from 'moment';
import TabsCustom from '@/components/_common/TabsCustom';
import ReplicateComplementDetailsCard from '../ReplicateComplementDetailsCard';
import ReplicateComplementFormView from '../ReplicateComplementFormView';
import { WorkOrderItem } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import ReplicateComplementItemsList from './../ReplicateComplementItemsList/index';
import { DateTimeFormat } from '@/utils/moment';
import { useWorkOrderItems } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';

export interface ComplementReplicateFormData {
  complement: {
    companyId?: number;
    estimatorId: number;
    type: WorkOrderComplementType;
    customerType: CustomerType;
    insurerId?: number;
    accidentNumber?: string;
    insurerDeductibleAmount?: number;
    entryDate?: Moment;
    authorizationDate?: Moment;
    closingDate?: Moment;
    estimatedLeaveDate?: Moment;
    returnDate?: Moment;
    prisma?: string;
    currentKilometers?: number;
    tankLevel?: WorkOrderComplementTankLevel;
  };
  items?: WorkOrderItem[];
}

interface Props extends Omit<ModalProps, 'onCancel' | 'onOk'> {
  onCancel: () => void;
  onOk: (obj: ComplementReplicateRequest) => void;
}

const ReplicateComplementModal: React.FC<Props> = ({
  open,
  confirmLoading,
  onCancel,
  onOk,
}) => {
  const [form] = Form.useForm<ComplementReplicateFormData>();

  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [activeTab, setActiveTab] = useState<string>('general');

  const {
    workorder: {
      data: workOrder,
      isLoading: isLoadingWorkOrder,
      dataUpdatedAt: workOrderDataUpdatedAt,
    },
    complement: {
      data: complement,
      isLoading: isLoadingComplement,
      dataUpdatedAt: complementDataUpdatedAt,
    },
  } = useWorkorderDetailContext();

  const items = useWorkOrderItems(open ? complement?.id : undefined);

  function getFormObj(
    complementData: WorkOrderComplement,
    workorderData: WorkOrder,
  ) {
    if (complementData && workorderData) {
      const formObj: ComplementReplicateFormData = {
        complement: {
          type: complementData.type,
          customerType: complementData.customerType,
          companyId: workorderData.company.id,
          estimatorId: complementData.estimator?.id,
          entryDate: complementData.entryDate
            ? moment(complementData.entryDate)
            : undefined,
          authorizationDate: complementData.authorizationDate
            ? moment(complementData.authorizationDate)
            : undefined,
          closingDate: complementData.closingDate
            ? moment(complementData.closingDate)
            : undefined,
          estimatedLeaveDate: complementData.estimatedLeaveDate
            ? moment(complementData.estimatedLeaveDate)
            : undefined,
          returnDate: complementData.returnDate
            ? moment(complementData.returnDate)
            : undefined,
          insurerId: complementData.insurer?.id,
        },
        items: items?.data?.data?.filter((item) => !item.deletedAt),
      };

      return formObj;
    }

    return {};
  }

  useEffect(() => {
    if (!open || !complement || !workOrder) return;

    const obj = getFormObj(complement, workOrder);

    form.setFieldsValue(obj);
  }, [open, complementDataUpdatedAt, workOrderDataUpdatedAt]);

  const handleOk = async () => {
    const values = await form.validateFields();

    if (values && workOrder && complement) {
      const { complement: complementValues, items } = values;

      const obj: ComplementReplicateRequest = {
        workOrderId: workOrder.id,
        workOrderComplementId: complement.id,
        status: complement.status,
        authorizationDate:
          complementValues.authorizationDate?.format(DateTimeFormat),
        estimatedLeaveDate:
          complementValues.estimatedLeaveDate?.format(DateTimeFormat),
        returnDate: complementValues.returnDate?.format(DateTimeFormat),
        prisma: complementValues.prisma,
        currentKilometers: complementValues.currentKilometers,
        tankLevel: complementValues.tankLevel,
        items: items
          ?.filter((item) => selectedKeys.includes(item.id))
          ?.map((item) => ({
            ...item,
            itemDescription: item.description,
            manufacturerId: item.manufacturer?.id,
            subAreaId: item.subArea?.id,
            supplierId: item.supplier?.id,
          })),
      };

      await onOk(obj);

      handleCancel();
    }
  };

  const handleCancel = () => {
    setSelectedKeys([]);
    setActiveTab('general');
    onCancel();
  };

  return (
    <Modal
      open={open}
      confirmLoading={confirmLoading}
      onCancel={handleCancel}
      width="90%"
      title="Replicando O.S"
      onOk={handleOk}
      okText="Replicar"
      destroyOnClose={!open}
    >
      <Form form={form} preserve={false}>
        <TabsCustom onChange={setActiveTab} activeKey={activeTab}>
          <TabsCustom.Item label="Dados Gerais" key="general">
            <Skeleton loading={isLoadingWorkOrder || isLoadingComplement}>
              <ReplicateComplementFormView form={form} />

              <Divider />

              <ReplicateComplementDetailsCard />
            </Skeleton>
          </TabsCustom.Item>

          <TabsCustom.Item label="Itens da O.S" key="items">
            <ReplicateComplementItemsList
              setSelectedKeys={setSelectedKeys}
              selectedKeys={selectedKeys}
            />
          </TabsCustom.Item>
        </TabsCustom>
      </Form>
    </Modal>
  );
};

export default ReplicateComplementModal;
